<template>
    <div class="profile-header">
        <div class="container">
            <div class="row mg-bottom-60">
                <div class="col-md-3 tw-flex tw-flex-col tw-items-center text-center">
                    <div class="avatar-border avatar-100 tw-rounded-full tw-relative tw-overflow-visible" :src="avatar" :alt="fullName"
                        :style="{
                            backgroundImage:`url(${avatar})`,
                            backgroundSize: 'cover'
                        }"
                    >
                        <span v-if="hasJerseyNumber" v-text="generalData.jersey_number" class="profile-jersey-number tw-absolute tw-bg-white tw-rounded-full"></span>
                    </div>

                    <h2 class="mg-bottom-10">{{ fullName }}</h2>

                    <p>
                        <span
                                v-text="title"
                                v-if="rules.is_super"
                        ></span>

                        <span
                                v-text="academy.name"
                                v-if="! rules.is_admin"
                        ></span>

                        <br>

                        <span
                                v-text="generalData.club_team_name"
                        ></span>
                    </p>

                    <div class="social-buttons text-center mg-top-20">
                        <social-media-icon
                                v-if="contactsData.twitter"
                                icon="twitter"
                                :url="`https://twitter.com/${contactsData.twitter}`"
                        />

                        <social-media-icon
                                v-if="contactsData.facebook"
                                icon="facebook-f"
                                :url="`https://facebook.com/${contactsData.facebook}`"
                        />

                        <social-media-icon
                                v-if="contactsData.instagram"
                                icon="instagram"
                                :url="`https://www.instagram.com/${contactsData.instagram}`"
                        />

                        <social-media-icon
                                v-if="contactsData.email"
                                icon="envelope"
                                fa-type="s"
                                :url="`mailto:${contactsData.email}`"
                        />
                    </div>
                </div>

                <div class="col-md-9 mg-top-20">
                    <template v-if="rules.is_super">
                        <p class="mg-top-80 pd-vert-20">
                            <small class="text-muted">{{ $t('wi.services_provided') }}</small>
                            <br>
                            <span v-text="servicesProvided"></span>
                        </p>
                        <p class="mg-20 pd-vert-20">
                            <small class="text-muted">{{ $t('wi.accepting_new_students') }}</small>
                            <br>
                            <span v-text="acceptingNewStudents"></span>
                        </p>
                    </template>

                    <template v-else>
                        <div class="block-grid-xs-2 block-grid-md-4">
                            <general-block
                                :label="$t('wi.birth_date')"
                                :value="fullBirthDate"
                                v-if="fullBirthDate"
                            />

                            <general-block
                                    :label="$t(`wi.graduating_${generalData.graduating_school.replace(/[\s.]+/g, '_') || ''}`.toLowerCase())"
                                    :value="generalData.graduating_year"
                                    v-if="generalData.graduating_school && generalData.graduating_year"
                            />

                            <general-block
                                :label="$t('wi.gender')"
                                :value="$t(`wi.gender_${generalData.gender}`.toLowerCase())"
                                v-if="generalData.gender && generalData.gender !== 'unstated'"
                            />

                            <general-block
                                    :label="$t('wi.height')"
                                    :value="generalData.height"
                                    v-if="generalData.height"
                            />

                            <general-block
                                    :label="$t('wi.weight')"
                                    :value="`${generalData.weight} lbs`"
                                    v-if="generalData.weight"
                            />

                            <general-block
                                    :label="$t('wi.primary_position')"
                                    :value="generalData.primary_position"
                                    v-if="generalData.primary_position"
                            />

                            <general-block
                                    :label="$t('wi.secondary_position')"
                                    :value="generalData.secondary_position"
                                    v-if="generalData.secondary_position"
                            />

                            <general-block
                                    :label="$t('wi.handicap')"
                                    :value="generalData.handicap"
                                    v-if="generalData.handicap"
                            />

                            <general-block
                                    :label="isAcademyGolf ? $t('wi.handedness') : $t('wi.swings')"
                                    :value="$t(`wi.${generalData.swings}`.replace(/\s+/g, '_').toLowerCase())"
                                    v-if="generalData.swings && generalData.swings !== 'Unstated'"
                            />

                            <general-block
                                    :label="$t('wi.hitter_type')"
                                    :value="generalData.hitter_type"
                                    v-if="isAcademySoftball && generalData.hitter_type && generalData.hitter_type != 'unstated'"
                            />

                            <general-block
                                    :label="$t('wi.throws')"
                                    :value="$t(`wi.${generalData.throws.toLowerCase()}`)"
                                    v-if="generalData.throws"
                            />

                            <general-block
                                    :label="$t('wi.bat_speed')"
                                    :value="`${generalData.bat_speed} MPH`"
                                    v-if="generalData.bat_speed"
                            />

                            <general-block
                                    :label="$t('wi.exit_velocity')"
                                    :value="`${generalData.exit_velocity} MPH`"
                                    v-if="generalData.exit_velocity"
                            />
                        </div>
                    </template>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import moment from 'moment';
    import { mapGetters } from 'vuex';
    import SocialMediaIcon from '../../PlayerProfile/components/VisualElements/SocialMediaIcon';
    import GeneralBlock from '../../PlayerProfile/components/VisualElements/GeneralBlock';
    import academyMixin from '../../PlayerProfile/mixins/academy';

    export default {
        components: {
            SocialMediaIcon,
            GeneralBlock
        },

        mixins: [academyMixin],

        data() {
            return {
                birthDateData: {},
            }
        },

         created() {
            this.setBirthDate();
        },

         methods: {
            setBirthDate() {
                if(this.generalData.hasOwnProperty('birth_date')) {
                    let splitBirthDate = this.generalData.birth_date.split('-');
                        this.birthDateData = {
                            day: splitBirthDate[2],
                            month: splitBirthDate[1],
                            year: splitBirthDate[0],
                        }
                } else {
                    this.birthDateData = {
                        day: null,
                        month: null,
                        year: null,
                    }
                }
            },
        },

        computed: {
            ...mapGetters({
                generalData: 'player-profile/general',
                contactsData: 'player-profile/contacts',
                rules: 'player-profile/roles',
                academy: 'player-profile/academy',
                avatar: 'player-profile/avatar'
            }),

            hasJerseyNumber() {
                return this.generalData.hasOwnProperty('jersey_number') && this.generalData.jersey_number;
            },

            fullName() {
                return `${this.generalData.first_name} ${this.generalData.last_name}`;
            },

            title() {
                return this.generalData.hasOwnProperty('title') ? `${this.generalData.title} - ` : '';
            },

            servicesProvided() {
                return this.generalData.hasOwnProperty('services_provided') ? this.generalData.services_provided.join(' / ') : 'NA';
            },

            acceptingNewStudents() {
                return this.generalData.hasOwnProperty('accepting_new_students') ? this.generalData.accepting_new_students.join(' / ') : 'NA';
            },

            birthDate() {
                let {day, month, year} = this.birthDateData;

                return !! day && !! month && !! year ? `${year}-${month}-${day}` : undefined;
            },

            fullBirthDate() {
                if (this.birthDate !== undefined) {
                    let dateObject = moment(this.birthDate, 'YYYY-MM-DD');
                    let age = moment().diff(dateObject, 'years');
                    let dateString = dateObject.format('MMMM D, YYYY');

                    return `${dateString} (${this.$t('wi.age')} ${age})`;
                }

                return null;
            },
        },
    }
</script>
